import forge from 'node-forge';

/**
 * Access to the server's public certificate, which the envelope scheme uses to
 * RSA-wrap each request's AES session key (see `./envelope.js`).
 *
 * The certificate is fetched from the `getcertificatebyorigin` API by
 * `./fetch-certificate.js` and stored in the redux `certificate` slice. This
 * module is the read side: it pulls the string out of redux, parses it once,
 * and hands back a ready-to-use public key.
 *
 * Redux is read through the `window.__REDUX_STORE__` handle set in
 * `src/redux/Store.jsx` rather than through a hook, because `SecureClient` is a
 * plain function, not a React component. Going through the global also keeps
 * this module out of the store's import graph, which would otherwise be a cycle
 * (Store -> authSlice -> logout-api-call -> SecureClient -> here -> Store).
 */

// Parsing a certificate is an ASN.1 walk, so the result is memoised against the
// exact string it came from. A rotated certificate changes the string, which
// invalidates the cache automatically. Failures are cached too, so a malformed
// certificate is not re-parsed on every call site.
let cache = {
  source: null,
  publicKey: null,
  notAfter: null,
};
let failureReason = null;

const getReduxStore = () =>
  (typeof window !== 'undefined' && window.__REDUX_STORE__) || null;

/**
 * The raw Base64 DER certificate currently held in redux.
 * @returns {string | null}
 */
export const getStoredCertificate = () => {
  try {
    return getReduxStore()?.getState()?.certificate?.certificate || null;
  } catch (err) {
    return null;
  }
};

/**
 * Turns a Base64 DER X.509 certificate into the pieces we need. Exported so
 * `fetch-certificate.js` can validate a freshly fetched certificate *before*
 * committing it to redux — we never store something we cannot parse.
 * @param {string} certificateBase64 - Base64-encoded DER certificate
 * @returns {{publicKey: object, notAfter: Date}}
 */
export const parseCertificate = (certificateBase64) => {
  const der = forge.util.decode64(certificateBase64);
  const certificate = forge.pki.certificateFromAsn1(forge.asn1.fromDer(der));

  return {
    publicKey: certificate.publicKey,
    notAfter: certificate.validity.notAfter,
  };
};

const loadCertificate = () => {
  const source = getStoredCertificate();

  // Nothing fetched yet (first ever visit, or storage was cleared).
  if (!source) {
    failureReason = 'CERTIFICATE_NOT_FETCHED';
    cache = { source: null, publicKey: null, notAfter: null };
    return null;
  }

  // Same string as last time — reuse the parsed result (or the cached failure).
  if (cache.source === source) return cache.publicKey ? cache : null;

  try {
    const { publicKey, notAfter } = parseCertificate(source);
    cache = { source, publicKey, notAfter };
    failureReason = null;
  } catch (err) {
    cache = { source, publicKey: null, notAfter: null };
    failureReason = `CERTIFICATE_PARSE_ERROR: ${err && err.message}`;
  }

  return cache.publicKey ? cache : null;
};

/**
 * True when redux holds a certificate that parses cleanly and has not expired.
 * `SecureClient` calls this to decide whether the envelope scheme can be used
 * for a request at all.
 * @returns {boolean}
 */
export const isCertificateUsable = () => {
  const certificate = loadCertificate();
  if (!certificate) return false;

  if (certificate.notAfter.getTime() <= new Date().getTime()) {
    failureReason = `CERTIFICATE_EXPIRED: notAfter=${certificate.notAfter.toISOString()}`;
    return false;
  }

  failureReason = null;
  return true;
};

/**
 * Why the certificate is unusable, for datalake reporting. Null when fine.
 * @returns {string | null}
 */
export const getCertificateFailureReason = () => failureReason;

/**
 * The RSA public key used to wrap session keys.
 * Only call this after `isCertificateUsable()` has returned true.
 * @returns {object} node-forge public key
 */
export const getPublicKey = () => {
  const certificate = loadCertificate();
  if (!certificate) {
    throw new Error(failureReason || 'CERTIFICATE_UNAVAILABLE');
  }
  return certificate.publicKey;
};

/**
 * Expiry of the certificate currently in use, for diagnostics and for deciding
 * whether a refetch is due.
 * @returns {Date | null}
 */
export const getCertificateExpiry = () => {
  const certificate = loadCertificate();
  return certificate ? certificate.notAfter : null;
};
